import { ReactElement, useContext, useMemo, useState } from "react";
import {
  IonIcon,
  IonToggle,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import {
  addCircleOutline,
  bugOutline,
  chatbubbleEllipsesOutline,
  chevronDownOutline,
  chevronUpOutline,
  createOutline,
  informationCircleOutline,
  languageOutline,
  mailOutline,
  logOutOutline,
  notificationsOutline,
  schoolOutline,
  settingsOutline,
} from "ionicons/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import AuthContext from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import { isPasswordSignInMethod } from "../../types/profile";
import ChangeEmailModal from "../Profile/ChangeEmailModal";
import ChangeNicknameModal from "../Profile/ChangeNicknameModal";
import ChangePhoneNumberModal from "../Profile/ChangePhoneNumberModal";
import ChangePwdModal from "../Profile/ChangePasswordModal";
import LeaveStudyModal from "../Profile/LeaveStudyModal";
import LegalMentionModal from "../Profile/LegalMentionModal";
import ReportBugModal from "../Profile/ReportBugModal";
import AboutModal from "../Profile/AboutModal";
import LanguageSelectorModal from "../../components/molecules/LanguageSelector/LanguageSelectorModal/LanguageSelectorModal";
import { patchNotificationPreferences } from "../../services/user.service";
import { ensurePushNotificationsEnabled } from "../../helper/notifications";
import { ENV } from "../../environments/environment";
import { resetCaches } from "../../services/questionnaire.service";

type SectionId = "account" | "notifications" | "about";
type NotificationChannel = "email" | "sms" | "push";

const DisplayAccount = (): ReactElement => {
  const { t } = useTranslation();
  const { user, getCurrent, endSession } = useContext(AuthContext);
  const { study, selectedRole } = useConfig();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  const [isLeaveStudyModalOpen, setIsLeaveStudyModalOpen] = useState(false);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [isChangePhoneNumberModalOpen, setIsChangePhoneNumberModalOpen] =
    useState(false);
  const [isChangeNicknameModalOpen, setIsChangeNicknameModalOpen] =
    useState(false);
  const [isChangePwdModalOpen, setIsChangePwdModalOpen] = useState(false);
  const [isLegalMentionOpen, setIsLegalMentionOpen] = useState(false);
  const [isReportBugOpen, setIsReportBugOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [langModalVisible, setLangModalVisible] = useState(false);

  const [expanded, setExpanded] = useState<Record<SectionId, boolean>>({
    account: true,
    notifications: false,
    about: false,
  });
  const [notificationLoading, setNotificationLoading] = useState<Record<NotificationChannel, boolean>>({
    email: false,
    sms: false,
    push: false,
  });
  const [pushPermissionError, setPushPermissionError] = useState(false);

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      title: "mentalplus_account_page_title",
    });
  });

  const getValidEmail = (email?: string | null) => {
    if (!email) return null;
    if (/^user_.*@etudementalo\.fr$/.test(email)) return null;
    return email;
  };

  const getValidPhone = (phone?: string | null) => {
    if (!phone) return null;
    if (phone === "0607080910") return null;
    return phone;
  };

  const getValidNickname = (nickname?: string | null) => {
    if (!nickname) return null;
    if (!nickname.trim()) return null;
    return nickname;
  };

  const canChangePassword = useMemo(
    () => isPasswordSignInMethod(selectedRole?.access?.signInMode),
    [selectedRole]
  );

  const canChangeNickname = canChangePassword;

  const nickname = useMemo(
    () => getValidNickname(user?.attributes?.nickname),
    [user]
  );

  const validEmail = getValidEmail(user?.attributes?.email);
  const pendingEmail = getValidEmail(user?.pending_email);
  const validPhone = getValidPhone(user?.attributes?.phone_number);
  const pendingPhone = getValidPhone(user?.pending_phone_number);

  const displayEmail = pendingEmail || validEmail;
  const displayPhone = pendingPhone || validPhone;
  const hasEmailValue = Boolean(displayEmail);
  const hasPhoneValue = Boolean(displayPhone);
  const canEnableEmailNotifications = Boolean(displayEmail);
  const canEnableSmsNotifications = Boolean(displayPhone);

  const notificationPreferences = user?.notification_preferences;
  const emailNotificationsEnabled = notificationPreferences?.email ?? true;
  const smsNotificationsEnabled = notificationPreferences?.sms ?? true;
  const pushNotificationsEnabled = notificationPreferences?.push ?? true;
  const effectivePushNotificationsEnabled =
    user?.notification_effective_status?.push ?? pushNotificationsEnabled;

  const toggleSection = (id: SectionId) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const updateNotificationPreference = async (
    channel: NotificationChannel,
    value: boolean
  ) => {
    if (!study?.id || notificationLoading[channel]) return;

    setNotificationLoading((prev) => ({ ...prev, [channel]: true }));

    try {
      if (channel === "push" && value) {
        let publicKey = ENV.VAPID_PUBLIC_KEY;

        try {
          const response = await fetch(`/assets/keys/${study.id}-web-key.json`);
          if (response.ok) {
            const data = await response.json();
            publicKey = data?.public_key || publicKey;
          }
        } catch (_error) {
          // Keep ENV key fallback when study key file is unavailable.
        }

        const isGranted = await ensurePushNotificationsEnabled({
          studyId: study.id,
          public_key: publicKey,
        });

        if (!isGranted) {
          setPushPermissionError(true);
          return;
        }

        setPushPermissionError(false);
      }

      await patchNotificationPreferences(study.id, { [channel]: value });
      await getCurrent();
    } catch (error) {
      console.error("Failed to update notification preference", error);
    } finally {
      setNotificationLoading((prev) => ({ ...prev, [channel]: false }));
    }
  };

  const handleDisconnect = async () => {
    if (!study?.slug) return;

    await endSession();
    resetCaches();
    ionRouter.push(`/${study.slug}/welcome`, "root", "replace", {
      unmount: true,
    });
  };

  return (
    <ResonanceLayout isConnected>
      <PageWrap>
        <Card>
          <SectionHeaderButton type="button" onClick={() => toggleSection("account")}>
            <SectionHeaderLeft>
              <IonIcon icon={settingsOutline} />
              <span>{t("mentalplus_account_manage")}</span>
            </SectionHeaderLeft>
            <IonIcon
              icon={expanded.account ? chevronUpOutline : chevronDownOutline}
            />
          </SectionHeaderButton>

          {expanded.account && (
            <SectionBody>
              <ItemButton
                type="button"
                onClick={() => canChangePassword && setIsChangeEmailModalOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("profile_email")}</ItemTitle>
                  <ItemValue>
                    {displayEmail || t("profile_not_provided")}
                  </ItemValue>
                </ItemText>
                {canChangePassword && (
                  <IonIcon
                    icon={hasEmailValue ? createOutline : addCircleOutline}
                  />
                )}
              </ItemButton>

              <ItemButton
                type="button"
                onClick={() =>
                  canChangePassword && setIsChangePhoneNumberModalOpen(true)
                }
              >
                <ItemText>
                  <ItemTitle>{t("profile_phone")}</ItemTitle>
                  <ItemValue>{displayPhone || t("profile_not_provided")}</ItemValue>
                </ItemText>
                {canChangePassword && (
                  <IonIcon
                    icon={hasPhoneValue ? createOutline : addCircleOutline}
                  />
                )}
              </ItemButton>

              <ItemButton
                type="button"
                onClick={() => canChangeNickname && setIsChangeNicknameModalOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("profile_pseudonym")}</ItemTitle>
                  <ItemValue>{nickname || t("profile_not_provided")}</ItemValue>
                </ItemText>
                {canChangeNickname && <IonIcon icon={createOutline} />}
              </ItemButton>

              <ItemButton
                type="button"
                onClick={() => canChangePassword && setIsChangePwdModalOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("profile_change_password")}</ItemTitle>
                  <ItemValue>****************</ItemValue>
                </ItemText>
                {canChangePassword && <IonIcon icon={createOutline} />}
              </ItemButton>

              <TextOnlyRow
                type="button"
                onClick={() => setIsLeaveStudyModalOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("mentalplus_account_deactivate")}</ItemTitle>
                  <ItemValue>****************</ItemValue>
                </ItemText>
              </TextOnlyRow>
            </SectionBody>
          )}

          <Divider />

          <SectionHeaderButton
            type="button"
            onClick={() => toggleSection("notifications")}
          >
            <SectionHeaderLeft>
              <IonIcon icon={notificationsOutline} />
              <span>{t("mentalplus_account_notifications")}</span>
            </SectionHeaderLeft>
            <IonIcon
              icon={
                expanded.notifications ? chevronUpOutline : chevronDownOutline
              }
            />
          </SectionHeaderButton>

          {expanded.notifications && (
            <SectionBody>
              {canEnableEmailNotifications ? (
                <NotificationRow>
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_email")}</ItemTitle>
                    <ItemValue>{displayEmail}</ItemValue>
                  </ItemText>
                  <NotificationControl>
                    <IonIcon icon={mailOutline} />
                    <StyledToggle
                      checked={emailNotificationsEnabled}
                      disabled={notificationLoading.email}
                      onIonChange={(event) =>
                        updateNotificationPreference("email", event.detail.checked)
                      }
                    />
                  </NotificationControl>
                </NotificationRow>
              ) : (
                <ItemButton
                  type="button"
                  onClick={() =>
                    canChangePassword && setIsChangeEmailModalOpen(true)
                  }
                  disabled={!canChangePassword}
                >
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_email")}</ItemTitle>
                    <ItemValue>
                      {t("mentalplus_account_notification_add_contact")}
                    </ItemValue>
                  </ItemText>
                  {canChangePassword && <IonIcon icon={addCircleOutline} />}
                </ItemButton>
              )}

              {canEnableSmsNotifications ? (
                <NotificationRow>
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_sms")}</ItemTitle>
                    <ItemValue>{displayPhone}</ItemValue>
                  </ItemText>
                  <NotificationControl>
                    <IonIcon icon={chatbubbleEllipsesOutline} />
                    <StyledToggle
                      checked={smsNotificationsEnabled}
                      disabled={notificationLoading.sms}
                      onIonChange={(event) =>
                        updateNotificationPreference("sms", event.detail.checked)
                      }
                    />
                  </NotificationControl>
                </NotificationRow>
              ) : (
                <ItemButton
                  type="button"
                  onClick={() =>
                    canChangePassword && setIsChangePhoneNumberModalOpen(true)
                  }
                  disabled={!canChangePassword}
                >
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_sms")}</ItemTitle>
                    <ItemValue>
                      {t("mentalplus_account_notification_add_contact")}
                    </ItemValue>
                  </ItemText>
                  {canChangePassword && <IonIcon icon={addCircleOutline} />}
                </ItemButton>
              )}

              <NotificationRow>
                <ItemText>
                  <ItemTitle>{t("mentalplus_account_notification_push")}</ItemTitle>
                  <ItemValue>
                    {pushPermissionError
                      ? t("mentalplus_account_notification_push_permission_required")
                      : t("mentalplus_account_notification_push_hint")}
                  </ItemValue>
                </ItemText>
                <NotificationControl>
                  <IonIcon icon={notificationsOutline} />
                  <StyledToggle
                    checked={effectivePushNotificationsEnabled}
                    disabled={notificationLoading.push}
                    onIonChange={(event) =>
                      updateNotificationPreference("push", event.detail.checked)
                    }
                  />
                </NotificationControl>
              </NotificationRow>
            </SectionBody>
          )}

          <Divider />

          <DirectRowButton type="button" disabled>
            <SectionHeaderLeft>
              <IonIcon icon={schoolOutline} />
              <span>{t("mentalplus_account_tutorials")}</span>
            </SectionHeaderLeft>
          </DirectRowButton>

          <Divider />

          <DirectRowButton type="button" onClick={() => setLangModalVisible(true)}>
            <SectionHeaderLeft>
              <IonIcon icon={languageOutline} />
              <span>{t("mentalplus_account_language")}</span>
            </SectionHeaderLeft>
          </DirectRowButton>

          <Divider />

          <SectionHeaderButton type="button" onClick={() => toggleSection("about")}>
            <SectionHeaderLeft>
              <IonIcon icon={informationCircleOutline} />
              <span>{t("mentalplus_account_about")}</span>
            </SectionHeaderLeft>
            <IonIcon icon={expanded.about ? chevronUpOutline : chevronDownOutline} />
          </SectionHeaderButton>

          {expanded.about && (
            <SectionBody>
              <TextOnlyRow
                type="button"
                onClick={() => setIsLegalMentionOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("profile_information_note")}</ItemTitle>
                </ItemText>
              </TextOnlyRow>

              <TextOnlyRow type="button" onClick={() => setIsAboutOpen(true)}>
                <ItemText>
                  <ItemTitle>{t("mentalplus_menu_equipe")}</ItemTitle>
                </ItemText>
              </TextOnlyRow>

              <TextOnlyRow type="button" disabled>
                <ItemText>
                  <ItemTitle>{t("mentalplus_account_app_version")}</ItemTitle>
                  <ItemValue>Mental+ 1.0</ItemValue>
                </ItemText>
              </TextOnlyRow>
            </SectionBody>
          )}

          <Divider />

          <DirectRowButton type="button" onClick={() => setIsReportBugOpen(true)}
          >
            <SectionHeaderLeft>
              <IonIcon icon={bugOutline} />
              <span>{t("mentalplus_account_contact")}</span>
            </SectionHeaderLeft>
          </DirectRowButton>
        </Card>

        <LogoutButton type="button" onClick={handleDisconnect}>
          <IonIcon icon={logOutOutline} />
          <span>{t("menu_logOut")}</span>
        </LogoutButton>

        <LeaveStudyModal
          onDismiss={() => setIsLeaveStudyModalOpen(false)}
          isOpen={isLeaveStudyModalOpen}
        />
        <ChangePwdModal
          onDismiss={() => setIsChangePwdModalOpen(false)}
          isOpen={isChangePwdModalOpen}
        />
        <ChangeEmailModal
          onDismiss={() => setIsChangeEmailModalOpen(false)}
          isOpen={isChangeEmailModalOpen}
          hasExistingEmail={Boolean(validEmail)}
        />
        <ChangePhoneNumberModal
          onDismiss={() => setIsChangePhoneNumberModalOpen(false)}
          isOpen={isChangePhoneNumberModalOpen}
          hasExistingPhoneNumber={Boolean(validPhone)}
        />
        <ChangeNicknameModal
          onDismiss={() => setIsChangeNicknameModalOpen(false)}
          isOpen={isChangeNicknameModalOpen}
        />
        <LegalMentionModal
          onDismiss={() => setIsLegalMentionOpen(false)}
          isOpen={isLegalMentionOpen}
        />
        <LanguageSelectorModal
          modalVisible={langModalVisible}
          onCloseClick={() => setLangModalVisible(false)}
        />
        <ReportBugModal
          isOpen={isReportBugOpen}
          onDismiss={() => setIsReportBugOpen(false)}
          user={user}
          study={study}
        />
        <AboutModal
          isOpen={isAboutOpen}
          onDismiss={() => setIsAboutOpen(false)}
        />
      </PageWrap>
    </ResonanceLayout>
  );
};

const PageWrap = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 1.1rem 1rem 1rem;
`;

const Card = styled.section`
  background: #f5f7f9;
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid #dce5ea;
`;

const SectionHeaderButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.2rem;
  border: 0;
  background: transparent;
  color: #0c2742;
  font-size: 1rem;
  font-weight: 600;

  &:disabled {
    cursor: default;
    opacity: 1;
  }
`;

const DirectRowButton = styled(SectionHeaderButton)`
  justify-content: flex-start;
`;

const SectionHeaderLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;

  ion-icon {
    color: #647ea5;
    font-size: 1.9rem;
  }

  span {
    font-size: clamp(1rem, 2.7vw, 1.2rem);
  }
`;

const SectionBody = styled.div`
  padding: 0 1.2rem 0.6rem;
`;

const LogoutButton = styled.button`
  width: 100%;
  min-height: 58px;
  margin: 1rem 0 0;
  border: none;
  border-radius: 30px;
  background: #ffce48;
  color: #1b1b1b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  padding: 0 20px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);

  ion-icon {
    font-size: 1.4rem;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ItemButton = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  border: 0;
  background: transparent;
  padding: 0.8rem 0;
  color: #0c2742;

  ion-icon {
    flex-shrink: 0;
    color: #647ea5;
    font-size: 1.8rem;
    margin-top: 0.2rem;
  }
`;

const NotificationRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 0.8rem 0;
  color: #0c2742;
`;

const NotificationControl = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;

  ion-icon {
    flex-shrink: 0;
    color: #647ea5;
    font-size: 1.35rem;
  }
`;

const StyledToggle = styled(IonToggle)`
  --handle-width: 18px;
  --handle-height: 18px;
  --track-background: #b8c6d5;
  --track-background-checked: #2d9f75;
`;

const TextOnlyRow = styled(ItemButton)`
  ion-icon {
    display: none;
  }

  &:disabled {
    opacity: 1;
    cursor: default;
  }
`;

const ItemText = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  min-width: 0;
`;

const ItemTitle = styled.span`
  font-size: clamp(1rem, 2.9vw, 1.1rem);
  font-weight: 600;
  color: #0c2742;
  text-align: left;
`;

const ItemValue = styled.span`
  font-size: clamp(0.95rem, 2.7vw, 1.05rem);
  color: #647ea5;
  text-align: left;
  word-break: break-word;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid #dce5ea;
  margin: 0;
`;

export default DisplayAccount;